import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import type { FieldErrors } from "react-hook-form";
import { useDataFetch } from "../../../hooks/use-data-fetch";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { saveSession } from "./use-signin-form";

// Definir la interfaz para los datos del formulario
export enum DocumentType {
  CC = "CC",
  CE = "CE",
  PASSPORT = "PASSPORT",
}

export interface SignUpFormData {
  referralCode?: string;
  firstName: string;  
  lastName: string;
  documentType:DocumentType;
  document: string;
  country_id: string;
  email: string;
  cellphone: string; 
  birthDay: number; 
  birthMonth: number;
  birthYear: number;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

// El código del link (?code=) se recuerda: el flujo con Google puede volver a
// /sign-up sin la URL original (p. ej. desde el login) y se perdía.
const REFERRAL_CODE_KEY = "multyhealth_referral_code";
const storage = {
  get: () => {
    try { return localStorage.getItem(REFERRAL_CODE_KEY); } catch { return null; }
  },
  set: (code: string) => {
    try { localStorage.setItem(REFERRAL_CODE_KEY, code); } catch {}
  },
  clear: () => {
    try { localStorage.removeItem(REFERRAL_CODE_KEY); } catch {}
  },
};

export const useSignUpForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Datos de Google: llegan desde el login ("Continuar con Google" con un
  // correo sin cuenta) o desde el botón "Registrarse con Google" de esta página.
  const location = useLocation();
  const [google, setGoogle] = useState<
    { credential: string; email: string; name: string } | undefined
  >((location.state as any)?.google);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [activationMessage, setActivationMessage] = useState<string>("");
  const [registeredEmail, setRegisteredEmail] = useState<string>("");
  const [isReferralCodeDisabled, setIsReferralCodeDisabled] = useState<boolean>(false);
  const [referralCode, setReferralCode] = useState<string>("");
  const [referralUserName, setReferralUserName] = useState<string>("");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    reset, getValues
  } = useForm<SignUpFormData>({
    defaultValues: {
      referralCode: "",
      country_id: "",
      email: "",
      documentType: DocumentType.CC,
      document: "",
      password: "",
      confirmPassword: "",
      acceptTerms: false,
      firstName: "",
      lastName: "",
      cellphone: "", 
      birthDay: undefined as any,
      birthMonth: undefined as any,
      birthYear: undefined as any,
    }
  });

  const { getData, postData } = useDataFetch();

  const { mutateAsync } = postData("/users", {});
  const { mutateAsync: googleLogin } = postData("/users/login/google", {});

  const {
    data: countriesData,
    isLoading: isLoadingCountries,
    error: countriesError
  } = getData(
    ["countries"],
    "/product/all/countries",
    {
      enabled: true,
    }
  );

  // Fetch referral code information
  const {
    data: referralData,
    isLoading: isLoadingReferralCode,
    refetch: refetchReferralCode,
    error: referralError
  } = getData(
    [`referralCode-${referralCode}`],
    referralCode ? `/users/get/multyhealth_code/${referralCode}` : '',
    {
      enabled: false,
    }
  );

  // Handle referral code change
  const handleReferralCodeChange = (e: React.FocusEvent<HTMLInputElement>) => {
    const code = e.target.value.trim();
    if (code) {
      setReferralCode(code);
    } else {
      setReferralCode('');
      setReferralUserName('');
    }
  };


  // Función para manejar el envío del formulario
  const onSubmit = async (data: SignUpFormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);
      setActivationMessage("");

      // Validar que el código de referido no esté vacío
      if (!data.referralCode || data.referralCode.trim() === '') {
        setSubmitError("El código de referido es obligatorio");
        setIsSubmitting(false);
        return;
      }

      // Validar que el código de referido sea válido (que tenga información del usuario)
      if (!referralData?.data?.user?.name) {
        setSubmitError("El código del referido no es correcto. Por favor verifica el código ingresado.");
        setIsSubmitting(false);
        return;
      }

      // Asegurarse de que las contraseñas coincidan
      if (!google && data.password !== data.confirmPassword) {
        setSubmitError("Las contraseñas no coinciden");
        setIsSubmitting(false);
        return;
      }

      const birthDate = new Date(
        data.birthYear,
        data.birthMonth - 1,
        data.birthDay
      );

      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();

      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age < 18) {
        setSubmitError("Debes ser mayor de 18 años");
        setIsSubmitting(false);
        return;
      }

      const dateOfBirth = `${data.birthYear}-${String(data.birthMonth).padStart(2, "0")}-${String(data.birthDay).padStart(2, "0")}`;

      const signUpData = {
        reference_id: data.referralCode,
        country_id: data.country_id,
        name: `${data.firstName} ${data.lastName}`,
        email: data.email,
        documentType: data.documentType,
        document: data.document,
        cellphone: data.cellphone,      
        dateOfBirth: dateOfBirth, 
        password: google ? undefined : data.password,
        google_credential: google?.credential,
        accept_terms: data.acceptTerms
      };

      // Llamar al método postData para registrar al usuario
      const result = await mutateAsync(signUpData);
      storage.clear();

      // Cuenta vinculada con Google: ya está confirmada, se inicia sesión.
      if (google) {
        const login = await googleLogin({ credential: google.credential });
        if (login?.message === "token_create_success" && login.data?.token) {
          saveSession(login.data);
          navigate("/store", { replace: true });
        } else {
          navigate("/login", { replace: true });
        }
        return result;
      }

      // Establecer mensaje de éxito con instrucciones de activación
      setSubmitSuccess(true);
      setRegisteredEmail(data.email);
      setReferralCode('');
      setReferralUserName('');
      setActivationMessage(
        "¡Registro exitoso! Hemos enviado un correo electrónico a tu dirección de email. "
        + "Por favor, revisa tu bandeja de entrada y sigue las instrucciones para activar tu cuenta."
      );

      reset(); // Limpiar el formulario

      return result;
    } catch (error: any) {
      if (error?.message === "user_create_precondition_failed") {
        setSubmitError("No se puede crear el usuario, por favor intente nuevamente");
      } else if (error?.message === "user_create_conflict_email") {
        setSubmitError("El correo electrónico ya está en uso");
      } else if (error?.message === "user_create_google_invalid_token") {
        setGoogle(undefined);
        setSubmitError("Tu sesión de Google expiró. Vuelve a pulsar \"Registrarse con Google\"; tus datos se conservan.");
      } else if (error?.message === "user_create_code_not_found") {
        setSubmitError("El código del referido no es correcto. Por favor verifica el código ingresado.");
      } else {
        setSubmitError(error?.message || "Ha ocurrido un error durante el registro");
      }
      setIsSubmitting(false);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check for code in URL when component mounts
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      storage.set(codeFromUrl);
      setValue('referralCode', codeFromUrl);
      setReferralCode(codeFromUrl);
      setIsReferralCodeDisabled(true);
      return;
    }
    // Sin código en la URL: se precarga el recordado, pero editable.
    const storedCode = storage.get();
    if (storedCode) {
      setValue('referralCode', storedCode);
      setReferralCode(storedCode);
    }
  }, [searchParams, setValue]);

  // "Registrarse con Google": si el correo ya tiene cuenta inicia sesión (no se
  // duplican cuentas); si no, precarga correo y nombre y el resto se completa.
  const signUpWithGoogle = async (credential: string) => {
    setSubmitError(null);
    try {
      const result = await googleLogin({ credential });
      if (result?.message === "user_google_not_registered") {
        setGoogle({ credential, ...result.data });
        return;
      }
      if (result?.message === "token_create_success" && result.data?.token) {
        saveSession(result.data);
        navigate("/store", { replace: true });
        return;
      }
      // Cuenta existente con verificación en dos pasos: se completa en el login.
      navigate("/login", { replace: true });
    } catch {
      setSubmitError("No pudimos validar tu cuenta de Google. Intenta nuevamente.");
    }
  };

  const cancelGoogle = () => {
    setGoogle(undefined);
    setValue("email", "");
  };

  // Precarga los datos que entrega Google.
  useEffect(() => {
    if (!google) return;
    const [firstName, ...lastName] = (google.name || "").trim().split(/\s+/);
    setValue("email", google.email);
    setValue("firstName", firstName || "");
    setValue("lastName", lastName.join(" "));
  }, [google, setValue]);

  // Effect to fetch referral code data when referralCode changes
  useEffect(() => {
    if (referralCode) {
      refetchReferralCode();
    }
  }, [referralCode, refetchReferralCode]);

  // Effect to update referral user name when data is fetched
  useEffect(() => {
    if (referralData?.data?.user) {
      const userName = referralData.data.user.name || '';
      setReferralUserName(userName);
    } else {
      setReferralUserName('');
    }
  }, [referralData]);

  return {
    googleEmail: google?.email,
    signUpWithGoogle,
    cancelGoogle,
    isSubmitting,
    submitError,
    submitSuccess,
    activationMessage,
    registeredEmail,
    isReferralCodeDisabled,
    isLoadingReferralCode,
    referralUserName,
    referralError,

    countriesData,
    isLoadingCountries,
    countriesError,

    errors: errors as FieldErrors<SignUpFormData>,

    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    onSubmit,
    resetForm: reset,
    handleReferralCodeChange
  };
};
